/**
 * The Activity page's load (firm-model spec §9, §10.2): a page of runs or actions for the
 * current filters, reloaded whenever they change (stale answers are dropped). The page stays
 * wiring + JSX.
 */
import type { ActivityResponse } from "@contract/firm/activity";
import * as React from "react";
import { ApiError } from "../api/client";
import type * as activityApi from "../api/activity";
import { toQuery, type ActivityFilters } from "./activityModel";

type Api = typeof activityApi;

/** A load failure as copy. A 404 means the panel shipped before the API (deploy skew). */
function loadErrorCopy(e: unknown): string {
  if (e instanceof ApiError && e.status === 404) return "Activity isn't available yet. Try again in a few minutes.";
  return e instanceof ApiError ? e.message : "We couldn't load activity. Try again.";
}

export function useActivity(api: Api, filters: ActivityFilters) {
  const [data, setData] = React.useState<ActivityResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const key = JSON.stringify(toQuery(filters));

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .fetchActivity(JSON.parse(key))
      .then((next) => {
        if (!alive) return;
        setData(next);
        setLoadError(null);
      })
      .catch((e: unknown) => alive && setLoadError(loadErrorCopy(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [api, key]);

  return { data, loading, loadError };
}
